import React, { useEffect, useState } from 'react';
import { Users, Inbox } from 'lucide-react';
import PageHeader from '../shared/PageHeader';
import ContactInbox from '../AccountWorkspace/ContactInbox';
import ContactsListTab from './ContactsListTab';
import '../shared/shared-chrome.css';
import './Contacts.css';

/*
 * ContactsPage — /contacts, the TekStak mirror of the GAS ContactsView
 * (App.jsx ~5189): the CRM contact table is the home, the Gmail-harvest
 * review queue lives behind a second tab.
 *
 * Tabs are hash-synced (#tab=<key>) using the same pattern as
 * Accounts/AccountsTabs.js so links and reload preserve the active tab.
 *
 * Props are optional — the page renders standalone. When MainApp later
 * wires `departments` + `onDepartmentSelect`, the list's department
 * badges become navigable (acronym → department_id lookup); without
 * them the badges are plain labels.
 */

export const CONTACT_TABS = [
  { key: 'list', label: 'Contacts', icon: Users },
  { key: 'review', label: 'Sync & review', icon: Inbox },
];

const HASH_PREFIX = '#tab=';

function readHash() {
  if (typeof window === 'undefined') return CONTACT_TABS[0].key;
  const h = window.location.hash;
  if (!h.startsWith(HASH_PREFIX)) return CONTACT_TABS[0].key;
  const candidate = h.slice(HASH_PREFIX.length);
  return CONTACT_TABS.find((t) => t.key === candidate) ? candidate : CONTACT_TABS[0].key;
}

export function useContactTabs() {
  const [activeKey, setActiveKey] = useState(readHash);

  // Back/forward navigation and pasted links.
  useEffect(() => {
    const onHash = () => setActiveKey(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const setTab = (key) => {
    if (!CONTACT_TABS.find((t) => t.key === key)) return;
    setActiveKey(key);
    // replaceState rather than location.hash so tab clicks don't pollute
    // the history stack (same rationale as AccountsTabs/VendorTabs).
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.hash = `${HASH_PREFIX}${key}`;
      window.history.replaceState({}, '', url);
    }
  };

  return [activeKey, setTab];
}

function ContactsTabs({ activeKey, onChange }) {
  return (
    <div className="contacts-tabs" role="tablist" aria-label="Contacts sections">
      {CONTACT_TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            className={`contacts-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => onChange(tab.key)}
          >
            <Icon size={14} />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function ContactsPage({ departments, onDepartmentSelect }) {
  const [tab, setTab] = useContactTabs();

  return (
    <div className="contacts-page">
      <PageHeader title="Contacts" blurb="People across your accounts" />

      <ContactsTabs activeKey={tab} onChange={setTab} />

      {tab === 'list' && (
        <div className="contacts-panel" role="tabpanel">
          <ContactsListTab
            departments={departments}
            onDepartmentSelect={onDepartmentSelect}
            onGoReview={() => setTab('review')}
          />
        </div>
      )}

      {tab === 'review' && (
        <div className="contacts-panel" role="tabpanel">
          {/* Trust-boundary explainer: the Gmail scan itself is in-tenant
              GAS code (Contact Radar) — only contact-identity fields reach
              this backend (apps/gas/CLAUDE.md carve-out #1). */}
          <div className="ts-card contacts-review-explainer">
            <h3 className="ts-card-title">Pending contact candidates</h3>
            <p className="contacts-review-note">
              The Gmail harvest itself runs in-tenant (the GAS cockpit&apos;s
              Contact Radar) — only contact identity fields are staged here.
              Nothing enters the CRM until you approve it below; rejecting a
              candidate also suppresses future harvests of that sender.
            </p>
          </div>
          <ContactInbox />
        </div>
      )}
    </div>
  );
}
